/**
 * Admin Dashboard Design Tokens
 *
 * Single source of truth for all admin page styling.
 * Based on specs/DESIGN_TOKENS.md and specs/UI_FOUNDATION_SPECIFICATION.md.
 * Reference implementation: AdminProductManagement.tsx
 *
 * Rules:
 * - Cards: shadow only, NO borders
 * - Headings: League Spartan
 * - Body: Inter
 * - Active states: 6% opacity Cedar green
 * - Border radius: 14px cards, 10px buttons/inputs, 6px small elements
 */

export const T = {
  // Brand colors
  cedarPrimary: '#1F6C4F',
  cedarDark: '#113C2B',
  cedarInk: '#0B1614',
  cedarStream: '#42A5A5',
  cedarSubtle: '#E8F2ED',
  cedarMist: '#A8D1BF',
  limestone: '#F3F3F3',

  // Text
  textPrimary: '#0B1614',
  textSecondary: '#6B7280',
  textTertiary: '#9CA3AF',

  // Semantic
  success: '#22C55E',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#42A5A5',

  // Radius
  radiusLg: '14px',
  radiusMd: '10px',
  radiusSm: '6px',
  radiusPill: '9999px',

  // Shadows (no borders)
  shadowMd: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
  shadowLg: '0px 4px 12px rgba(0, 0, 0, 0.15)',

  // Fonts
  fontDisplay: "'League Spartan', sans-serif",
  fontBody: "'Inter', system-ui, sans-serif",
} as const;

/** Card — shadow only, no border */
export const card: React.CSSProperties = {
  background: '#FFFFFF',
  borderRadius: T.radiusLg,
  boxShadow: T.shadowMd,
  padding: '16px 20px',
};

/** Section label — 12px uppercase tracking */
export const sectionLabel: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 700,
  color: T.textTertiary,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: '10px',
  fontFamily: T.fontBody,
};

/** Page/section heading — League Spartan */
export const heading: React.CSSProperties = {
  fontFamily: T.fontDisplay,
  fontWeight: 600,
  color: T.cedarInk,
};

/** Icon box — small colored background container */
export const iconBox = (bg: string): React.CSSProperties => ({
  padding: '6px',
  borderRadius: T.radiusSm,
  backgroundColor: bg,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/** Primary button inline styles */
export const btnPrimary: React.CSSProperties = {
  backgroundColor: T.cedarPrimary,
  color: '#FFFFFF',
  border: 'none',
  borderRadius: T.radiusMd,
  fontWeight: 600,
  cursor: 'pointer',
};

/** Badge/pill — no border, subtle bg */
export const badge = (bg: string, text: string): React.CSSProperties => ({
  fontSize: '11px',
  fontWeight: 500,
  padding: '3px 8px',
  borderRadius: T.radiusPill,
  backgroundColor: bg,
  color: text,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
});

/** Active state for nav/tabs — 6% opacity Cedar */
export const activeState: React.CSSProperties = {
  backgroundColor: 'rgba(31, 108, 79, 0.06)',
  color: T.cedarPrimary,
};
